// /tutors — карта «Рядом с домом» для родителей (задача 8, решения владельца
// 06.10.2026): на компьютере список и карта рядом, фильтры строкой сверху,
// на телефоне список + кнопка «Карта». Цена на пине, кластеры при отдалении,
// «Онлайн» — отдельная вкладка. Телефон — по клику, с сервера.
// Блок «Не нашли рядом? Оставьте заявку» — письмо, без таблицы; пока
// партнёров мало, он стоит и над пустым списком.
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";

import { MAP_COPY, TUTORS_PATH } from "./map-copy";
import { MAP_UX } from "./map-ux-copy";
import { FEATURED_COPY } from "./featured-copy";
import { MapCanvas, NARROW_QUERY, type MapArea, type MapPoint } from "./MapCanvas";
import { LOW_ACCURACY_M, clearHome, districtFromGoogle, formatKm, nearestSpots, placeByDistance, readHome, saveHome, type Me, type Spot } from "./my-location";
import { loadMaps } from "./PinPicker";
import { parseMapQuery, type MapQuery } from "./map-query";
import { MEET_LINE, showMeetLine } from "./meet-line";
import { TurnstileFallback, TurnstileWidget, type TurnstileHandle } from "@/components/security/TurnstileWidget";
import { SiteFooter, SiteHeader, TUTORS_JOIN_HREF, type SiteLang } from "@/components/site/SiteChrome";
import { BAKU_CITY, DISTRICTS, FORMATS, GRADES, METRO, TUTOR_QUICK, categoriesFor, labelOf, servicesFor, subjectItemsFor, type PartnerKind } from "@/data/map-taxonomy";
import { DISTRICT_LABELS } from "@/lib/map-places";
import { alsoTravelsLine, comesHomeTo } from "@/lib/map-building-travel";
import { ALL_CITIES, cityLabel, cityOfPartner, filterCities, inTab } from "@/lib/map-cities";
import { pinPrice, priceFilter, priceText } from "@/lib/price-format";
import { getMapsBrowserKey } from "@/lib/map-partners.functions";
import { trackContactClick, type ContactSource } from "@/lib/map-contact-click";
import { isMyPartner } from "@/lib/contact-self";
import { getPartnerPhone, listMapCities, listPublicPartners, submitMapRequest, type PublicPartner } from "@/lib/map-public.functions";
import { logPartnerEvent, watchImpression } from "@/lib/map-partner-events-client";
import { FEATURED_MAX, FEATURED_NARROW, pickFeatured } from "@/lib/map-featured";
import { initialsOf } from "@/lib/map-photo";
import { toggleSaved, useSavedIds } from "@/lib/tutor-saved";
import { searchFold } from "@/lib/search-fold";
import { contactOk } from "@/lib/map-match";
import { teachesInLabel } from "@/lib/map-match-emails";
import { readUtm } from "@/lib/utm";
import { subjectLabel, subjectPageCodes, subjectPath } from "@/lib/tutor-subject-pages";

type Lang = SiteLang;

const BAKU_CENTER = { lat: 40.4093, lng: 49.8671 };
/**
 * Стартовый кадр без геолокации: Баку и Абшерон (Xırdalan … Mərdəkan). Масштаб,
 * а не fitBounds: на старте карта ещё без размера, и подгонка давала zoom 8 —
 * пол-страны. Телефон (390 px) — 9, шире — 10: оба кадра покрывают ~49.45…50.3.
 */
const BAKU_ABSHERON = { lat: 40.42, lng: 49.87 };
const bakuAbsheronZoom = () => (typeof window !== "undefined" && window.innerWidth < 700 ? 9 : 10);

/**
 * Анкета в видимой области карты (26.10.2026). С меткой — по точке. Без метки
 * (выезд к ученику) — если в области центр одного из её районов выезда, а вне
 * Баку — центр её города. Онлайн-анкеты сюда не попадают (вкладка «Onlayn»).
 */
export function inMapArea(p: PublicPartner, a: MapArea, cities: readonly { code: string; lat: number; lng: number }[]): boolean {
  const inside = (lat: number, lng: number) => lat >= a.s && lat <= a.n && lng >= a.w && lng <= a.e;
  if (p.lat !== null && p.lng !== null && p.address_mode === "building") return inside(p.lat, p.lng);
  const centres = DISTRICTS as Array<{ code: string; lat?: number; lng?: number }>;
  for (const code of p.travel_districts) {
    const d = centres.find((x) => x.code === code);
    if (d?.lat !== undefined && d.lng !== undefined && inside(d.lat, d.lng)) return true;
  }
  const c = cities.find((x) => x.code === (p.city || BAKU_CITY));
  return !!c && inside(c.lat, c.lng);
}

/**
 * Блок «Рекомендуемые» (02.11.2026): на каких вкладках он есть, по слою.
 * «Dərnəklər» (кружки) пока без блока — включить, добавив вкладки сюда.
 */
const NO_FEATURED: readonly string[] = [];

export const FEATURED_TABS: Record<PartnerKind, readonly ("near" | "online")[]> = { tutor: ["near", "online"], club: [] };

/** Телефон: высота свёрнутой шторки списка (ручка + «N repetitor»), px. Та же — в tutors-map.css. */
const SHEET_PEEK = 64;
/** Подпись Баку, пока справочник map_cities не пришёл (одна строка вместо пустого списка). */
const BAKU_NAME = { az: "Bakı", ru: "Баку", en: "Baku" } as const;

/** Цена на карточке: «15 ₼ / dərs · 60 dəq», мелко «≈ 5 ₼ / dərs»; не подтверждено — только сумма. */
export function PriceTag({ p, lang }: { p: PublicPartner; lang: Lang }) {
  const t = priceText(p, lang);
  if (t.main === null) return null;
  // «60 ₼ / мес» — жирным одной строкой, подробности (уроки, минуты, ≈) — мелко ниже:
  // на телефоне длинная строка целиком сжимала имя и рвала «50 ₼» на две строки.
  const [head, ...rest] = t.main.split(" · ");
  const detail = rest.join(" · ");
  return (
    <span className="tm-card__price">
      <b>{head}</b>
      {detail && <span>{detail}</span>}
      {t.sub && <span>{t.sub}</span>}
    </span>
  );
}

export function placeOf(p: PublicPartner, lang: Lang) {
  const c = MAP_COPY[lang];
  if (p.address_mode === "online") return c.online;
  // Вне Баку районов и метро нет: город вместо них (выезд — по городу целиком).
  const city = cityOfPartner(p);
  if (city && city !== BAKU_CITY) {
    // Подпись — из map_cities (ставит сервер); нет её — код.
    const name = p.city_name?.[lang] ?? city;
    if (p.address_mode === "travel") return `${c.travelsTo}: ${name}`;
    // Абшерон: посёлок по границам (26.10.2026) — «Masazır, Abşeron»; метро — если рядом.
    const where = p.district ? `${labelOf(DISTRICT_LABELS, p.district, lang)}, ${name}` : name;
    return p.metro ? `${where}, ${c.nearMetro} ${labelOf(METRO, p.metro, lang)}` : where;
  }
  if (p.address_mode === "travel") return `${c.travelsTo}: ${p.travel_districts.map((d) => labelOf(DISTRICT_LABELS, d, lang)).join(", ")}`;
  const d = p.district ? labelOf(DISTRICT_LABELS, p.district, lang) : "";
  return d + (p.metro ? `, ${c.nearMetro} ${labelOf(METRO, p.metro, lang)}` : "");
}

export const subjectsOf = (p: PublicPartner, lang: Lang) => {
  const items = subjectItemsFor(p.kind);
  return p.subjects.map((s) => labelOf(items, s, lang)).join(" · ");
};

/**
 * «WhatsApp» и «Позвонить»: номер приходит с сервера только по нажатию.
 * showPhone = false («Telefon nömrəmi göstər» выключен в кабинете) — только WhatsApp.
 * self — с этого устройства входили в кабинет анкеты: клик не считается обращением родителя.
 */
export function ContactButtons({ id, lang, source, showPhone = true, placement }: { id: string; lang: Lang; source: ContactSource; showPhone?: boolean; placement?: CardPlacement }) {
  const c = MAP_COPY[lang];
  const getPhone = useServerFn(getPartnerPhone);
  const [err, setErr] = useState(false);
  const go = async (how: "call" | "wa") => {
    setErr(false);
    const kind = how === "wa" ? "whatsapp" : "call";
    trackContactClick(kind, source, id);
    if (placement) logPartnerEvent(id, how === "wa" ? "whatsapp_click" : "call_click", placement);
    try {
      const r = await getPhone({ data: { id, kind, source, self: isMyPartner(id) } });
      const digits = (how === "wa" ? r.whatsapp : r.phone).replace(/[^\d+]/g, "");
      window.location.href = how === "wa" ? `https://wa.me/${digits.replace(/^\+/, "")}` : `tel:${digits}`;
    } catch {
      setErr(true);
    }
  };
  return (
    <div className="tm-contact">
      <button type="button" className="hp-btn tm-btn" onClick={() => go("wa")}>
        {c.whatsapp}
      </button>
      {showPhone && (
        <button type="button" className="hp-btn hp-btn--outline tm-btn" onClick={() => go("call")}>
          {c.call}
        </button>
      )}
      {err && <span className="tm-err">{c.phoneError}</span>}
    </div>
  );
}

/** ♥ — сохранить анкету в этом браузере (src/lib/tutor-saved.ts), без входа. */
export function SaveHeart({ id, lang }: { id: string; lang: Lang }) {
  const u = MAP_UX[lang];
  const on = useSavedIds().includes(id);
  return (
    <button type="button" className="tm-heart" aria-pressed={on} aria-label={on ? u.unsave : u.save} title={on ? u.unsave : u.save} onClick={(e) => (e.stopPropagation(), toggleSaved(id))}>
      <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 20.5s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.6a4.3 4.3 0 0 1 7.5 2.7c0 5.6-7.5 10.2-7.5 10.2Z" fill={on ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

/**
 * Карточка выбранной метки: в окошке у метки (компьютер) и в нижней шторке
 * (телефон). Имя, цена с единицей, предметы, район/метро, WhatsApp и «Ətraflı».
 * Номер — с сервера по нажатию, как у кнопок в списке.
 */
export function MapPopupCard({ p, lang, onClose }: { p: PublicPartner; lang: Lang; onClose: () => void }) {
  const c = MAP_COPY[lang];
  const getPhone = useServerFn(getPartnerPhone);
  const [err, setErr] = useState(false);
  const wa = async () => {
    setErr(false);
    trackContactClick("whatsapp", "map", p.id);
    try {
      const r = await getPhone({ data: { id: p.id, kind: "whatsapp", source: "map", self: isMyPartner(p.id) } });
      window.location.href = `https://wa.me/${r.whatsapp.replace(/[^\d]/g, "")}`;
    } catch {
      setErr(true);
    }
  };
  return (
    <div className="tm-pop" role="dialog" aria-label={p.name}>
      <div className="tm-pop__top">
        <PartnerAvatar p={p} size={40} />
        <b className="tm-pop__name">{p.name}</b>
        <SaveHeart id={p.id} lang={lang} />
        <button type="button" className="tm-pop__x" onClick={onClose} aria-label={c.closeCard}>
          ×
        </button>
      </div>
      {p.price_azn !== null && <PriceTag p={p} lang={lang} />}
      {p.trial_free && <span className="tm-trial">{c.trialFree}</span>}
      <span className="tm-card__subj">{subjectsOf(p, lang)}</span>
      <span className="tm-muted">{placeOf(p, lang)}</span>
      <div className="tm-pop__actions">
        <button type="button" className="hp-btn tm-btn" onClick={wa}>
          {c.whatsapp}
        </button>
        <a className="hp-btn hp-btn--outline tm-btn" href={`${TUTORS_PATH[lang]}/${p.slug}`}>
          {c.more}
        </a>
      </div>
      {err && <span className="tm-err">{c.phoneError}</span>}
    </div>
  );
}

/**
 * Фото партнёра или инициалы. Место под аватар есть у КАЖДОЙ карточки:
 * анкета без фото не выглядит «хуже» и не стоит ниже — порядок и пометки от
 * фото не зависят. Фото не загрузилось — тоже инициалы.
 */
export function PartnerAvatar({ p, size = 44 }: { p: PublicPartner; size?: number }) {
  const [broken, setBroken] = useState(false);
  const style = { width: size, height: size, borderRadius: p.kind === "tutor" ? "50%" : 12 };
  return p.photo_url && !broken ? (
    <img className="tm-avatar tm-avatar--img" src={p.photo_url} alt="" width={size} height={size} loading="lazy" style={style} onError={() => setBroken(true)} />
  ) : (
    <span className="tm-avatar" style={style} aria-hidden="true">
      {initialsOf(p.name)}
    </span>
  );
}

/** Где карточка на /tutors — для событий map_partner_events: блок «Рекомендуемые» или общий список. */
type CardPlacement = "featured" | "list";

/** Метка «Tövsiyə olunur» и ⓘ: по нажатию — кто это (без слов о качестве). */
function FeaturedBadge({ lang }: { lang: Lang }) {
  const f = FEATURED_COPY[lang];
  const [open, setOpen] = useState(false);
  return (
    <span className="tm-feat" onClick={(e) => e.stopPropagation()}>
      <span className="tm-feat__badge">{f.badge}</span>
      <button type="button" className="tm-feat__i" aria-label={f.infoBtn} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        ⓘ
      </button>
      {open && (
        <span className="tm-feat__info" role="note">
          {f.info}
        </span>
      )}
    </span>
  );
}

/**
 * Карточка партнёра. profile — страница анкеты /tutors/<slug>: без ссылки
 * «Подробнее» (она ведёт на эту же страницу), аватар можно подменить
 * (крупное фото с лайтбоксом). track — карточка на /tutors: показы и клики
 * уходят в map_partner_events (блок «Рекомендуемые» или список); featured —
 * карточка блока, с меткой.
 */
export function PartnerCard({ p, lang, active, onPick, avatarSize, profile, avatar, distance, preview, hover, onHover, track, featured, narrowOnly }: { p: PublicPartner; lang: Lang; active?: boolean; onPick?: () => void; avatarSize?: number; profile?: boolean; avatar?: React.ReactNode; distance?: string | null; preview?: boolean; hover?: boolean; onHover?: (id: string | null) => void; track?: CardPlacement; featured?: boolean; narrowOnly?: boolean }) {
  const c = MAP_COPY[lang];
  // Один общий IntersectionObserver на все карточки (map-partner-events-client.ts); ref стабилен, пока не сменились id и место.
  const watch = useCallback((el: HTMLElement | null) => (track ? watchImpression(el, p.id, track) : undefined), [p.id, track]);
  const extra =
    p.kind === "tutor"
      ? p.grades.map((g) => labelOf(GRADES, g, lang)).join(", ")
      : p.age_min || p.age_max
        ? `${[p.age_min, p.age_max].filter(Boolean).join("–")} ${c.years}`
        : "";
  return (
    <article
      ref={track ? watch : undefined}
      id={narrowOnly ? undefined : `p-${p.id}`}
      className={`tm-card${narrowOnly ? " tm-card--narrow-only" : ""}${active ? " tm-card--active" : ""}${hover ? " tm-card--hover" : ""}${profile ? " tm-card--profile" : ""}${featured ? " tm-card--featured" : ""}`}
      onClick={onPick}
      onMouseEnter={onHover && (() => onHover(p.id))}
      onMouseLeave={onHover && (() => onHover(null))}
    >
      <div className="tm-card__top">
        {avatar ?? <PartnerAvatar p={p} size={avatarSize} />}
        {/* На странице профиля имя — единственный H1 страницы; в списке карты — просто жирное. */}
        {profile ? <h1 className="tm-card__name">{p.name}</h1> : <b className="tm-card__name">{p.name}</b>}
        {p.price_azn !== null && <PriceTag p={p} lang={lang} />}
      </div>
      {featured && <FeaturedBadge lang={lang} />}
      {distance && <span className="tm-dist">{distance}</span>}
      {p.trial_free && <span className="tm-trial">{c.trialFree}</span>}
      {showMeetLine(p) && <span className="tm-meet">{MEET_LINE[lang]}</span>}
      <span className="tm-card__subj">{subjectsOf(p, lang)}</span>
      <span className="tm-muted">{placeOf(p, lang)}</span>
      {alsoTravelsLine(p, lang) && (
        <span className="tm-muted">
          {c.alsoTravelsTo}: {alsoTravelsLine(p, lang)}
        </span>
      )}
      <span className="tm-muted">{[p.formats.map((f) => labelOf(FORMATS, f, lang)).join(", "), extra].filter(Boolean).join(" · ")}</span>
      {/* Предпросмотр анкеты (форма join): анкеты ещё нет — кнопок контактов и ссылки тоже. */}
      {!preview && (
      <div className="tm-card__actions" onClick={(e) => e.stopPropagation()}>
        <ContactButtons id={p.id} lang={lang} source={profile ? "profile" : "list"} showPhone={p.show_phone !== false} placement={track} />
        <span className="tm-card__end">
          {!profile && (
            <a className="tm-link" href={`${TUTORS_PATH[lang]}/${p.slug}`} onClick={track && (() => logPartnerEvent(p.id, "details_click", track))}>
              {c.more}
            </a>
          )}
          <SaveHeart id={p.id} lang={lang} />
        </span>
      </div>
      )}
    </article>
  );
}

/**
 * «Не нашли рядом? Оставьте заявку» — строка в map_parent_requests, подбор
 * 3–5 репетиторов, письмо родителю и копия админу (submitMapRequest →
 * map-match.server.ts). После отправки — те же карточки, что в письме. Экспорт — для пустого состояния
 * мини-карты на главной: там та же форма, а не копия. onSent — только для
 * события аналитики главной, на /tutors не передаётся.
 */
export function RequestForm({ lang, layer, title, preset, hideTitle, onSent }: { lang: Lang; layer: PartnerKind; title: string; preset: { subject: string; grade: string; district: string; city?: string }; hideTitle?: boolean; onSent?: () => void }) {
  const c = MAP_COPY[lang];
  const send = useServerFn(submitMapRequest);
  // Город заявки — весь справочник map_cities (родитель может искать там, где
  // анкет ещё нет). По умолчанию Bakı; «Вся страна» сюда не переносится.
  const fetchCities = useServerFn(listMapCities);
  const qc = useQuery({ queryKey: ["map-cities"], queryFn: () => fetchCities(), staleTime: 10 * 60 * 1000 });
  const ref = qc.data?.cities ?? [];
  const presetCity = preset.city && preset.city !== ALL_CITIES ? preset.city : BAKU_CITY;
  const [city, setCity] = useState(presetCity);
  const baku = city === BAKU_CITY;
  const [subject, setSubject] = useState(preset.subject);
  const [grade, setGrade] = useState(preset.grade);
  const [age, setAge] = useState("");
  const [district, setDistrict] = useState(preset.district);
  const [budget, setBudget] = useState("");
  const [budgetUnit, setBudgetUnit] = useState<"lesson" | "month">("lesson");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [format, setFormat] = useState<"any" | "tutor" | "home" | "online">("any");
  const [onlineOk, setOnlineOk] = useState(false);
  const [teachLang, setTeachLang] = useState<Lang>(lang);
  const [comment, setComment] = useState("");
  /** Ловушка для ботов — человек это поле не видит. */
  const [website, setWebsite] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [tsKey, setTsKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState<"idle" | "sent" | "error" | "rate" | "contact">("idle");
  const [result, setResult] = useState<{ status: string; emailed: boolean; partners: PublicPartner[]; otherLangs: Record<string, Lang[]> } | null>(null);
  const tutor = layer === "tutor";
  // Телефон ИЛИ почта с согласием (04.11.2026) — то же правило, что на сервере.
  // Кнопка всегда активна: без контакта по нажатию — подсказка, а не немая кнопка.
  const canSend = contactOk({ phone, email, consent });
  const form = useRef<HTMLFormElement>(null);
  const ts = useRef<TurnstileHandle>(null);
  const items = subjectItemsFor(layer);
  useEffect(() => {
    setSubject(preset.subject);
    setGrade(preset.grade);
    setDistrict(preset.district);
    setCity(presetCity);
  }, [preset.subject, preset.grade, preset.district, presetCity]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!canSend) {
      setState("contact");
      return;
    }
    setState("idle");
    setBusy(true);
    try {
      const tok = token ?? (await ts.current?.waitForToken()) ?? null;
      if (!tok) throw new Error("captcha");
      // Источник — ?src= или utm_source адреса страницы (только безопасные символы).
      const qs = new URLSearchParams(window.location.search);
      const src = (qs.get("src") ?? qs.get("utm_source") ?? "").trim();
      const r = await send({
        data: {
          layer,
          subject: subject ? labelOf(items, subject, "ru") : "",
          subject_code: subject || undefined,
          grade: layer === "tutor" ? grade : "",
          age: layer === "club" && age ? Number(age) : null,
          city,
          // Район — только у Баку.
          district: baku ? district : "",
          budget: budget ? Number(budget) : null,
          budget_unit: budgetUnit,
          phone: phone.trim(),
          email: email.trim(),
          consent: !!email.trim() && consent,
          format: tutor ? format : "any",
          online_ok: tutor && format !== "online" ? onlineOk : false,
          teach_lang: tutor ? teachLang : undefined,
          comment: comment.trim() || undefined,
          source: /^[a-z0-9_-]{1,40}$/i.test(src) ? src : undefined,
          // utm с первого рекламного захода (src/lib/utm.ts) — как в анкете репетитора.
          ...readUtm(),
          website,
          lang,
          turnstile_token: tok,
        },
      });
      setResult({ status: r.status, emailed: r.emailed, partners: r.partners, otherLangs: r.otherLangs ?? {} });
      setState("sent");
      onSent?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      setState(msg.includes("rate_limited") ? "rate" : msg.includes("contact_required") ? "contact" : "error");
      setToken(null);
      setTsKey((k) => k + 1);
    } finally {
      setBusy(false);
    }
  }

  if (state === "sent") {
    const matched = result?.partners ?? [];
    const line = result?.status === "no_match" ? c.reqNoMatch : result?.emailed ? c.reqSentTo.replace("{email}", email.trim()) : matched.length ? c.reqSentNoEmail : c.reqSent;
    return (
      <div className="tm-req tm-req--sent" role="status">
        <span>{line}</span>
        {matched.length > 0 && (
          <div className="tm-req__matched">
            <b className="tm-req__title">{c.reqMatchedTitle}</b>
            {matched.map((p) =>
              // Уроки не на языке заявки («Язык уроков» или язык анкеты, сервер считает сам) — так и пишем.
              result?.otherLangs[p.id] ? (
                <div key={p.id}>
                  <PartnerCard p={p} lang={lang} />
                  <span className="tm-req__lang-note">{teachesInLabel(lang, result.otherLangs[p.id]!)}</span>
                </div>
              ) : (
                <PartnerCard key={p.id} p={p} lang={lang} />
              ),
            )}
          </div>
        )}
      </div>
    );
  }
  return (
    <form ref={form} className="tm-req" onSubmit={onSubmit} noValidate>
      {!hideTitle && <b className="tm-req__title">{title}</b>}
      <span className="tm-muted">{tutor ? c.reqLeadTutor : c.reqLead}</span>
      {/* Ловушка для ботов: вне экрана, без фокуса, без автозаполнения. Имя не похоже на
          поле адреса: «company»/«website» браузер и менеджеры паролей заполняют (03.11.2026). */}
      <div className="tm-hp" aria-hidden="true">
        <label htmlFor="tm-req-hp-x">Leave this empty</label>
        <input id="tm-req-hp-x" type="text" name="tm_hp_x" tabIndex={-1} autoComplete="off" data-1p-ignore="" data-lpignore="true" data-bwignore="" data-form-type="other" value={website} onChange={(e) => setWebsite(e.target.value)} />
      </div>
      <div className="tm-req__grid">
        <label>
          {c.reqSubject}
          <select className="tj-in" value={subject} onChange={(e) => setSubject(e.target.value)}>
            <option value="">—</option>
            {items.map((x) => (
              <option key={x.code} value={x.code}>
                {x[lang]}
              </option>
            ))}
          </select>
        </label>
        {layer === "tutor" ? (
          <label>
            {c.reqGrade}
            <select className="tj-in" value={grade} onChange={(e) => setGrade(e.target.value)}>
              <option value="">—</option>
              {GRADES.map((g) => (
                <option key={g.code} value={g.code}>
                  {g[lang]}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label>
            {c.reqAge}
            <input className="tj-in" type="number" min={1} max={99} inputMode="numeric" value={age} onChange={(e) => setAge(e.target.value)} />
          </label>
        )}
        <label>
          {c.city}
          <select className="tj-in" value={city} onChange={(e) => setCity(e.target.value)}>
            {/* Справочник ещё не пришёл — один Bakı, выбор не теряется. */}
            {(ref.length ? ref.map((x) => x.code) : [BAKU_CITY]).map((code) => (
              <option key={code} value={code}>
                {ref.length ? cityLabel(ref, code, lang) : BAKU_NAME[lang]}
              </option>
            ))}
          </select>
        </label>
        {baku && (
          <label>
            {c.reqDistrict}
            <select className="tj-in" value={district} onChange={(e) => setDistrict(e.target.value)}>
              <option value="">—</option>
              {DISTRICTS.map((d) => (
                <option key={d.code} value={d.code}>
                  {d[lang]}
                </option>
              ))}
            </select>
          </label>
        )}
        {tutor && (
          <label>
            {c.reqFormat}
            <select className="tj-in" value={format} onChange={(e) => setFormat(e.target.value as typeof format)}>
              <option value="any">{c.reqFormatAny}</option>
              <option value="tutor">{c.reqFormatTutor}</option>
              <option value="home">{c.reqFormatHome}</option>
              <option value="online">{c.reqFormatOnline}</option>
            </select>
          </label>
        )}
        <label className="tm-req__budget-row">
          {c.reqBudget}
          <span className="tm-req__budget">
            <input className="tj-in" type="number" min={0} inputMode="numeric" value={budget} onChange={(e) => setBudget(e.target.value)} />
            <select className="tj-in" value={budgetUnit} onChange={(e) => setBudgetUnit(e.target.value as "lesson" | "month")} aria-label={c.reqBudget}>
              <option value="lesson">{c.reqPerLesson}</option>
              <option value="month">{c.reqPerMonth}</option>
            </select>
          </span>
        </label>
        {tutor && (
          <label>
            {c.reqTeachLang}
            <select className="tj-in" value={teachLang} onChange={(e) => setTeachLang(e.target.value as Lang)}>
              <option value="az">Azərbaycan</option>
              <option value="ru">Русский</option>
              <option value="en">English</option>
            </select>
          </label>
        )}
        <b className="tm-req__contact">{c.reqContactTitle}</b>
        <label>
          {c.reqPhone}
          <input className="tj-in" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+994 50 000 00 00" autoComplete="tel" />
        </label>
        <label>
          {c.reqEmail}
          <input className="tj-in" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@mail.az" autoComplete="email" inputMode="email" />
        </label>
      </div>
      {tutor && format !== "online" && (
        <label className="tm-req__check">
          <input type="checkbox" checked={onlineOk} onChange={(e) => setOnlineOk(e.target.checked)} />
          {c.reqOnlineOk}
        </label>
      )}
      {email.trim() && (
        <label className="tm-req__check">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          {c.reqConsent}
        </label>
      )}
      <label className="tm-req__comment">
        {c.reqComment}
        <textarea className="tj-in" rows={2} maxLength={500} value={comment} onChange={(e) => setComment(e.target.value)} />
      </label>
      <TurnstileWidget key={tsKey} lang={lang} startOn={form} handleRef={ts} onToken={setToken} onExpired={() => setToken(null)} onError={() => setToken(null)} />
      {state === "error" && (
        <>
          <p className="tm-err" role="alert">
            {c.reqError}
          </p>
          <TurnstileFallback lang={lang} />
        </>
      )}
      {state === "rate" && (
        <p className="tm-err" role="alert">
          {c.reqRateLimited}
        </p>
      )}
      {state === "contact" && !canSend && (
        <p className="tm-err" role="alert" data-testid="req-contact-hint">
          {c.reqContactNeeded}
        </p>
      )}
      <button type="submit" className="hp-btn" disabled={busy}>
        {busy ? c.reqSending : c.reqSend}
      </button>
    </form>
  );
}

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Поиск адреса или метро для метки «Evim» — тот же Google Places, что в форме
 * репетитора. Скрипт карт грузится только когда поле показано (после отказа
 * геолокации или по выбору родителя).
 */
/** Поле адреса Google — для кнопки «Ünvanı yaz»: курсор в него. */
const ADDRESS_BOX_ID = "tm-address";
function focusAddress(): boolean {
  const ac = document.querySelector<HTMLElement>(`#${ADDRESS_BOX_ID} gmp-place-autocomplete`);
  if (!ac) return false;
  ac.scrollIntoView({ block: "center" });
  ac.focus();
  return true;
}

function AddressSearch({ mapsKey, lang, placeholder, onPlace, autoFocus }: { mapsKey: string; lang: Lang; placeholder: string; onPlace: (p: Spot) => void; autoFocus?: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  const onPlaceRef = useRef(onPlace);
  onPlaceRef.current = onPlace;
  useEffect(() => {
    let cancelled = false;
    loadMaps(mapsKey, lang)
      .then(async (google: any) => {
        const places = await google.maps.importLibrary("places");
        if (cancelled || !box.current || !places.PlaceAutocompleteElement) return;
        const ac = new places.PlaceAutocompleteElement({ includedRegionCodes: ["az"] });
        ac.setAttribute("placeholder", placeholder);
        ac.style.width = "100%";
        box.current.replaceChildren(ac);
        // «Ünvanı yaz» нажали раньше, чем поле загрузилось, — курсор сюда сейчас.
        if (autoFocus) focusAddress();
        ac.addEventListener("gmp-select", async (ev: any) => {
          try {
            const place = ev.placePrediction.toPlace();
            await place.fetchFields({ fields: ["location"] });
            if (place.location) onPlaceRef.current({ lat: place.location.lat(), lng: place.location.lng() });
          } catch {
            /* можно отметить на карте */
          }
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsKey, lang]);
  return <div ref={box} id={ADDRESS_BOX_ID} className="tm-me__ac" />;
}

/** Район родителя по точке — Google Places (как в форме репетитора). Сервер не участвует. */
async function districtAt(mapsKey: string, lang: Lang, p: Spot): Promise<string | null> {
  try {
    const google: any = await loadMaps(mapsKey, lang);
    const { Place, SearchNearbyRankPreference } = await google.maps.importLibrary("places");
    const { places } = await Place.searchNearby({
      fields: ["addressComponents"],
      locationRestriction: { center: p, radius: 150 },
      maxResultCount: 3,
      rankPreference: SearchNearbyRankPreference.DISTANCE,
      language: "az",
    });
    for (const x of places ?? []) {
      const c = (x.addressComponents ?? []).find((a: any) => a.types?.includes("sublocality_level_1"));
      if (c?.longText) return districtFromGoogle(c.longText);
    }
  } catch {
    /* район не определился — анкеты «выезд» остаются в списке */
  }
  return null;
}

/* eslint-enable @typescript-eslint/no-explicit-any */

export type TutorsList = Awaited<ReturnType<typeof listPublicPartners>>;

/**
 * initial — список из loader маршрута: сервер рисует карточки, интро и ссылки на предметы сразу.
 * featured — случайный порядок рекомендуемых с сервера; query — фильтры из адреса (начальное состояние).
 */
export function TutorsMapPage({ lang, initial, featured: featuredOrder = NO_FEATURED, query: urlQuery }: { lang: Lang; initial?: TutorsList | null; featured?: readonly string[]; query?: MapQuery }) {
  const c = MAP_COPY[lang];
  const fetchList = useServerFn(listPublicPartners);
  const fetchKey = useServerFn(getMapsBrowserKey);
  const q = useQuery({ queryKey: ["map-partners-public"], queryFn: () => fetchList(), initialData: initial ?? undefined });
  const [mapsKey, setMapsKey] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    fetchKey()
      .then((r) => setMapsKey(r.key))
      .catch(() => setMapsKey(null));
  }, [fetchKey]);

  const subjectPages = useMemo(() => subjectPageCodes(q.data?.rows ?? []), [q.data]);
  // Начальные фильтры — из адреса через loader: сервер и браузер начинают с одного состояния.
  const [layer, setLayer] = useState<PartnerKind>(urlQuery?.layer ?? "tutor");
  const [tab, setTab] = useState<"near" | "online">("near");
  // Город карты — Баку по умолчанию; в фильтре — города, где есть анкеты, и «Вся страна».
  // Город по умолчанию — «Hamısı» (26.10.2026): что показывать, решает видимая область карты.
  const [city, setCity] = useState(urlQuery?.city || ALL_CITIES);
  const [subject, setSubject] = useState(urlQuery?.subject ?? "");
  const [district, setDistrict] = useState(urlQuery?.district ?? "");
  // «Придёт к нам домой»: район, куда партнёр выезжает (building с галочкой и travel).
  const [home, setHome] = useState("");
  const [format, setFormat] = useState("");
  const [grade, setGrade] = useState("");
  const [age, setAge] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [trial, setTrial] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const u = MAP_UX[lang];
  // Телефон (≤880 px, NARROW_QUERY) — «сначала карта» (25.10.2026, как Google Maps / Wolt):
  // строка поиска + «Filtrlər», под ней карта на всю высоту, список — шторкой снизу.
  // Сервер рисует компьютерную разметку; узкий экран узнаётся после загрузки —
  // от него зависят только кнопки и поля, не тексты страницы.
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(NARROW_QUERY);
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Строка поиска: предмет / район / метро из подсказки или свободный текст.
  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");
  const [metro, setMetro] = useState("");
  const [searchPick, setSearchPick] = useState<{ kind: "subject" | "district" | "metro"; code: string } | null>(null);
  // Наведение: карточка ↔ метка (компьютер).
  const [hoverId, setHoverId] = useState<string | null>(null);
  // «Искать в этой области»: список — только анкеты с меткой в этой области (вкладка «Рядом»).
  const [area, setArea] = useState<MapArea | null>(null);
  const savedIds = useSavedIds();

  // Телефон: список — нижняя шторка внутри области карты.
  // peek — видна ручка и «N repetitor» (карта), full — список поверх карты.
  // Тянуть шторку — за ручку; из списка, прокрученного до верха, — вниз.
  const [sheet, setSheet] = useState<"peek" | "full">("peek");
  const sheetRef = useRef<HTMLElement>(null);
  const splitRef = useRef<HTMLDivElement>(null);
  // Шторка: перетаскивание пальцем. Положения (от верха области карты): full — 0,
  // peek — видна полоса SHEET_PEEK внизу. Отпустили — к ближайшему, быстрый рывок — по направлению.
  useEffect(() => {
    const el = sheetRef.current;
    const split = splitRef.current;
    if (!el || !split || !narrow) return;
    const FULL = 0;
    const peekY = () => split.clientHeight - SHEET_PEEK;
    let mode: "none" | "maybe" | "drag" = "none";
    let handle = false;
    let y0 = 0;
    let t0 = 0;
    let top0 = 0;
    let y = 0;
    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const onHandle = !!(e.target as Element).closest(".tm-sheethead");
      const full = el.dataset.sheet === "full";
      // Тянуть — после сдвига на 8 px: касание ручки или кнопки остаётся нажатием.
      handle = onHandle;
      mode = onHandle || !full || el.scrollTop <= 0 ? "maybe" : "none";
      y0 = e.touches[0].clientY;
      t0 = performance.now();
      top0 = el.getBoundingClientRect().top - split.getBoundingClientRect().top;
      y = top0;
    };
    const onMove = (e: TouchEvent) => {
      if (mode === "none") return;
      const dy = e.touches[0].clientY - y0;
      if (mode === "maybe") {
        if (Math.abs(dy) < 8) return;
        // Свёрнутая шторка тянется в обе стороны; развёрнутая из списка — только вниз (вверх — прокрутка).
        if (!handle && el.dataset.sheet === "full" && dy < 0) {
          mode = "none";
          return;
        }
        mode = "drag";
      }
      e.preventDefault();
      y = Math.min(peekY(), Math.max(FULL, top0 + dy));
      el.style.transition = "none";
      el.style.transform = `translateY(${y}px)`;
    };
    const onEnd = (e: TouchEvent) => {
      if (mode !== "drag") {
        mode = "none";
        return;
      }
      mode = "none";
      const dy = (e.changedTouches[0]?.clientY ?? y0) - y0;
      el.style.transition = "";
      el.style.transform = "";
      // Касание без сдвига — это нажатие (ручку переключает её onClick).
      if (Math.abs(dy) < 8) return;
      const v = dy / Math.max(1, performance.now() - t0); // px/мс
      if (v < -0.5) setSheet("full");
      else if (v > 0.5) setSheet("peek");
      else setSheet(y < (FULL + peekY()) / 2 ? "full" : "peek");
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [narrow]);
  // Высота шапки сайта — отступ sticky-карты на компьютере. Телефон: где начинается
  // область карты — карта занимает экран от неё до низа (100dvh − --tm-split-top).
  const pageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const head = document.querySelector<HTMLElement>(".hp-header");
    const page = pageRef.current;
    if (!head || !page || typeof ResizeObserver === "undefined") return;
    const set = () => {
      page.style.setProperty("--tm-top", `${head.offsetHeight}px`);
      const split = splitRef.current;
      if (split) page.style.setProperty("--tm-split-top", `${Math.round(split.getBoundingClientRect().top + window.scrollY)}px`);
    };
    set();
    const ro = new ResizeObserver(set);
    ro.observe(head);
    for (const el of page.querySelectorAll<HTMLElement>(".tm-head, .tm-msearch")) ro.observe(el);
    window.addEventListener("resize", set);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", set);
    };
  }, []);

  // «Моё местоположение» (решение владельца 12.10). Геолокация — только по
  // нажатию. Координаты остаются в браузере: на сервер и в аналитику не идут.
  const [me, setMe] = useState<Me | null>(null);
  const [meStatus, setMeStatus] = useState<"idle" | "locating" | "denied" | "failed" | "low">("idle");
  const [lowKm, setLowKm] = useState("");
  const [pickMode, setPickMode] = useState(false);
  const [askAddress, setAskAddress] = useState(false);
  const [parentDistrict, setParentDistrict] = useState<string | null>(null);
  const [focus, setFocus] = useState<(Spot & { zoom: number; n: number; fit?: Spot[] }) | null>(null);
  // Родитель — в центре, три ближайшие метки — в кадре (а не просто масштаб 14).
  const nearestRef = useRef<(p: Spot) => Spot[]>(() => []);
  const focusOn = (p: Spot) => setFocus((f) => ({ ...p, zoom: 14, n: (f?.n ?? 0) + 1, fit: nearestRef.current(p) }));
  // Старт карты (26.10.2026): геолокация уже разрешена — от родителя (без запроса);
  // иначе — кадр «Баку + Абшерон». Метка «Evim» с прошлого визита важнее обоих.
  useEffect(() => {
    if (readHome()) return;
    setFocus((f) => f ?? { ...BAKU_ABSHERON, zoom: bakuAbsheronZoom(), n: 1 });
    const perms = (navigator as Navigator & { permissions?: { query: (d: { name: string }) => Promise<{ state: string }> } }).permissions;
    perms
      ?.query({ name: "geolocation" })
      .then((r) => {
        if (r.state === "granted") locate();
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Метка «Evim» с прошлого визита.
  useEffect(() => {
    const h = readHome();
    if (h) setMe({ ...h, kind: "home", accuracy: null });
  }, []);
  const placeHomePin = (p: Spot) => {
    saveHome(p);
    setMe({ ...p, kind: "home", accuracy: null });
    setMeStatus("idle");
    setPickMode(false);
    setAskAddress(false);
    focusOn(p);
  };
  const removeHome = () => {
    clearHome();
    setMe(null);
    setParentDistrict(null);
  };
  const locate = () => {
    if (!("geolocation" in navigator)) {
      setMeStatus("failed");
      setAskAddress(true);
      return;
    }
    setMeStatus("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const acc = pos.coords.accuracy;
        if (acc > LOW_ACCURACY_M) {
          setLowKm(formatKm(acc / 1000, lang));
          setMeStatus("low");
          setAskAddress(true);
          return;
        }
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setMe({ ...p, kind: "gps", accuracy: acc });
        setMeStatus("idle");
        setAskAddress(false);
        setPickMode(false);
        focusOn(p);
      },
      (err) => {
        setMeStatus(err.code === err.PERMISSION_DENIED ? "denied" : "failed");
        setAskAddress(true);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };
  const startPick = () => {
    setPickMode(true);
    // Телефон: окно фильтров закрыть — метку ставят на карте.
    setFiltersOpen(false);
    setSheet("peek");
  };
  // «Ünvanı yaz»: показать поле адреса (телефон — в окне «Filtrlər») и поставить в него курсор.
  // flushSync — чтобы поле стало видимым до focus() в том же нажатии: на iPhone
  // клавиатура открывается только от фокуса внутри жеста пользователя.
  const [addressFocusWanted, setAddressFocusWanted] = useState(false);
  const typeAddress = () => {
    flushSync(() => {
      if (narrow) setFiltersOpen(true);
      setPickMode(false);
      setAskAddress(true);
    });
    if (!focusAddress()) setAddressFocusWanted(true); // поле ещё грузится — фокус после загрузки
  };
  // Отказ, сбой или низкая точность геолокации — заметный блок с кнопкой, а не мелкая строка.
  const meNoticeText = meStatus === "denied" ? c.me.denied : meStatus === "failed" ? c.me.failed : meStatus === "low" ? c.me.lowAccuracy(lowKm) : null;
  const meNotice = meNoticeText ? (
    <div className="tm-notice" role="alert">
      <p>{meNoticeText}</p>
      {mapsKey && (
        <button type="button" className="hp-btn tm-notice__btn" onClick={typeAddress}>
          {c.me.typeAddress}
        </button>
      )}
    </div>
  ) : null;
  // Район родителя — для анкет «выезд по районам» без точки.
  useEffect(() => {
    if (!me || !mapsKey) {
      setParentDistrict(null);
      return;
    }
    let cancelled = false;
    districtAt(mapsKey, lang, me).then((d) => !cancelled && setParentDistrict(d));
    return () => {
      cancelled = true;
    };
  }, [me?.lat, me?.lng, mapsKey, lang]);
  // Фильтры из адреса (?type=&subject=&district= — их ставит главная): только
  // начальные значения при загрузке; дальше страница живёт как раньше.
  useEffect(() => {
    const f = parseMapQuery(window.location.search);
    if (f.layer) setLayer(f.layer);
    if (f.subject) setSubject(f.subject);
    if (f.district) setDistrict(f.district);
    if (f.city) setCity(f.city);
  }, []);

  const rows = useMemo(() => q.data?.rows ?? [], [q.data]);
  const ref = useMemo(() => q.data?.cities ?? [], [q.data]);
  const cities = useMemo(() => filterCities(rows, ref), [rows, ref]);
  const baku = city === BAKU_CITY;
  // Город из адреса, которого нет в фильтре (анкет там нет), — «Hamısı».
  useEffect(() => {
    if (q.isSuccess && city !== ALL_CITIES && !cities.includes(city)) setCity(ALL_CITIES);
  }, [q.isSuccess, cities, city]);
  const pickCity = (code: string) => {
    setCity(code);
    setDistrict("");
    setHome("");
    // Карта — на анкеты выбранного города (Баку — на центр, как при загрузке).
    const pts = rows.filter((p) => (code === ALL_CITIES ? cityOfPartner(p) !== null : cityOfPartner(p) === code) && p.lat !== null && p.lng !== null);
    // «Вся страна» — по всем анкетам на карте и крупнее масштаб.
    if (code === ALL_CITIES) {
      setFocus((f) => ({ ...(pts.length ? { lat: (Math.min(...pts.map((p) => p.lat!)) + Math.max(...pts.map((p) => p.lat!))) / 2, lng: (Math.min(...pts.map((p) => p.lng!)) + Math.max(...pts.map((p) => p.lng!))) / 2 } : BAKU_CENTER), zoom: 7, n: (f?.n ?? 0) + 1 }));
      return;
    }
    const center = code === BAKU_CITY || !pts.length ? BAKU_CENTER : { lat: pts.reduce((a, p) => a + p.lat!, 0) / pts.length, lng: pts.reduce((a, p) => a + p.lng!, 0) / pts.length };
    setFocus((f) => ({ ...center, zoom: 12, n: (f?.n ?? 0) + 1 }));
  };
  // Выбранный на карте пин — карточка в видимую часть списка. На компьютере
  // прокручивается страница (карта sticky и не уезжает), только если карточки
  // не видно; отступ под шапку — scroll-margin-top в CSS. На телефоне — шторка
  // с мини-карточкой, список не трогаем.
  useEffect(() => {
    if (!activeId || window.matchMedia(NARROW_QUERY).matches) return;
    const card = document.getElementById(`p-${activeId}`);
    if (!card) return;
    const r = card.getBoundingClientRect();
    const top = pageRef.current ? parseFloat(getComputedStyle(pageRef.current).getPropertyValue("--tm-top")) || 0 : 0;
    if (r.top >= top && r.bottom <= window.innerHeight) return;
    card.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [activeId]);
  // Выбрали метку на телефоне — шторка вниз, мини-карточка над ней.
  useEffect(() => {
    if (activeId) setSheet("peek");
  }, [activeId]);
  const base = useMemo(
    () =>
      rows.filter((p) => {
        if (p.kind !== layer) return false;
        if (!inTab(p, tab, city)) return false;
        if (subject && !p.subjects.includes(subject)) return false;
        // Районы — бакинские коды; фильтр действует при любом городе (по умолчанию «Hamısı»).
        if (tab === "near" && district && p.district !== district && !p.travel_districts.includes(district)) return false;
        if (tab === "near" && layer === "tutor" && home && !comesHomeTo(p, home)) return false;
        if (format && !p.formats.includes(format)) return false;
        if (layer === "tutor" && grade && !p.grades.includes(grade)) return false;
        if (layer === "club" && age) {
          const a = Number(age);
          if ((p.age_min !== null && a < p.age_min) || (p.age_max !== null && a > p.age_max)) return false;
        }
        // Фильтр «цена урока до» (вариант A): известная цена урока выше — скрыть; неизвестная — оставить, но в конце.
        if (priceFilter(p, priceMax ? Number(priceMax) : null) === "over") return false;
        if (trial && !p.trial_free) return false;
        // Строка поиска телефона: метро из подсказки и свободный текст (имя, предметы, место).
        if (metro && p.metro !== metro) return false;
        if (query && !searchFold([p.name, subjectsOf(p, lang), placeOf(p, lang)].join(" ")).includes(searchFold(query))) return false;
        return true;
      }),
    [rows, layer, tab, city, subject, district, home, format, grade, age, priceMax, trial, metro, query, lang],
  );
  // Список и число в шторке — анкеты в видимой области карты (26.10.2026, как у Airbnb).
  // Метки на карте — из base: иначе метки за краем пропали бы и их не найти сдвигом.
  // Пока карта не сообщила область (не загрузилась, вкладка «Onlayn») — без неё.
  const shown = useMemo(() => (area && tab === "near" ? base.filter((p) => inMapArea(p, area, ref)) : base), [base, area, tab, ref]);
  // Местоположение известно — ближние выше, у карточек расстояние (вкладка «Рядом»).
  const placedAll = tab === "near" ? placeByDistance(shown, me, parentDistrict) : shown.map((p) => ({ p, km: null as number | null }));
  // Блок «Рекомендуемые» (02.11.2026). Случайный порядок — с сервера (loader,
  // partner-route.ts), поэтому SSR и первый рендер в браузере совпадают, и блок
  // стоит на месте с первой отрисовки: ни запроса после гидрации, ни сдвига.
  // В блоке — первые до 4 по этому порядку среди прошедших фильтры и вкладку
  // (base: без области карты — иначе блок менялся бы, когда карта догрузится).
  // На телефоне 4-я скрыта CSS и стоит в списке на своём месте (tm-card--narrow-only).
  // Из списка ниже карточки блока убираются, счётчик — прежний.
  const featRank = useMemo(() => new Map(featuredOrder.map((id, i) => [id, i])), [featuredOrder]);
  const featuredOn = FEATURED_TABS[layer].includes(tab) && featRank.size > 0;
  const featured = useMemo(() => (featuredOn ? pickFeatured(base.map((p) => ({ p })), featRank, FEATURED_MAX) : []), [featuredOn, base, featRank]);
  const kmOf = new Map(placedAll.map((x) => [x.p.id, x.km]));
  // Всегда вне списка — первые 3; 4-я — в списке только на телефоне.
  const inBlock = new Set(featured.slice(0, FEATURED_NARROW).map((x) => x.p.id));
  const fourth = featured[FEATURED_NARROW]?.p.id ?? null;
  // При фильтре по цене анкеты без известной цены урока — после остальных, под подписью.
  const maxNum = priceMax ? Number(priceMax) : null;
  const placed = placedAll.filter((x) => priceFilter(x.p, maxNum) !== "unknown" && !inBlock.has(x.p.id));
  const placedUnknown = maxNum === null ? [] : placedAll.filter((x) => priceFilter(x.p, maxNum) === "unknown" && !inBlock.has(x.p.id));
  // Метки — из base (не из области карты); рекомендуемые — крупнее, с зелёной обводкой.
  const points: MapPoint[] = useMemo(
    () =>
      base
        .filter((p) => p.address_mode === "building" && p.lat !== null && p.lng !== null)
        // Цена с единицей, как в карточке: «80 ₼/ay», «15 ₼/dərs»; формат не подтверждён — только сумма.
        .map((p) => ({ id: p.id, lat: p.lat!, lng: p.lng!, label: pinPrice(p, lang) ?? p.name.slice(0, 12), club: p.kind === "club", featured: featuredOn && featRank.has(p.id) })),
    [base, lang, featuredOn, featRank],
  );
  nearestRef.current = (me: Spot) => nearestSpots(me, points, 3);
  // Выбранная метка — карточка в окошке (компьютер) и в шторке (телефон, режим «Карта»).
  const activeP = activeId ? (base.find((p) => p.id === activeId) ?? null) : null;
  const closeCard = () => setActiveId(null);

  const reset = () => {
    setSubject("");
    setDistrict("");
    setHome("");
    setFormat("");
    setGrade("");
    setAge("");
    setPriceMax("");
    setTrial(false);
    clearSearch();
  };
  const pickLayer = (k: PartnerKind) => {
    setLayer(k);
    setSubject("");
    setGrade("");
    setAge("");
    setHome("");
  };
  const preset = { subject, grade, district, city };

  // Строка поиска телефона: подсказки — предметы слоя, районы Баку, метро.
  const clearSearch = () => {
    setSearchText("");
    setQuery("");
    setMetro("");
    setSearchPick(null);
  };
  // «×» в строке: снять и то, что поставила подсказка (предмет или район).
  const clearSearchPick = () => {
    if (searchPick?.kind === "subject" && subject === searchPick.code) setSubject("");
    if (searchPick?.kind === "district" && district === searchPick.code) setDistrict("");
    clearSearch();
  };
  const suggestions = useMemo(() => {
    const t = searchFold(searchText);
    if (t.length < 2 || query) return [];
    const subj = [...servicesFor(layer), ...categoriesFor(layer).flatMap((cat) => cat.items)].map((x) => ({ kind: "subject" as const, code: x.code, label: x[lang] }));
    const dist = baku || city === ALL_CITIES ? DISTRICTS.map((d) => ({ kind: "district" as const, code: d.code, label: d[lang] })) : [];
    const met = baku || city === ALL_CITIES ? METRO.map((m) => ({ kind: "metro" as const, code: m.code, label: m[lang] })) : [];
    return [...subj, ...dist, ...met].filter((x) => searchFold(x.label).includes(t)).slice(0, 8);
  }, [searchText, query, layer, lang, baku, city]);
  const pickSuggestion = (x: { kind: "subject" | "district" | "metro"; code: string; label: string }) => {
    setSearchText(x.label);
    setQuery("");
    setSearchPick({ kind: x.kind, code: x.code });
    if (x.kind === "subject") setSubject(x.code);
    else if (x.kind === "district") setDistrict(x.code);
    else setMetro(x.code);
    setSheet("peek");
  };
  // Сколько фильтров включено — число на кнопке «Filtrlər».
  const activeFilters = [subject, district, home, format, grade, age, priceMax, trial ? "1" : "", tab === "online" ? "1" : "", city !== ALL_CITIES ? "1" : "", layer === "club" ? "1" : ""].filter(Boolean).length;

  // «Siz buradasınız / Evim · ближние выше» и поле адреса «Evim» — в списке (компьютер) и в окне «Filtrlər» (телефон).
  const meNow = me ? (
    <span className="tm-me__now">
      {me.kind === "gps" ? c.me.youAreHere : c.me.home} · {c.me.sorted}
      {me.kind === "home" && (
        <button type="button" className="tj-plain tm-me__rm" onClick={removeHome}>
          {c.me.remove}
        </button>
      )}
    </span>
  ) : null;
  const meAddress = askAddress ? (
    mapsKey ? (
      <div className="tm-me__fallback">
        <AddressSearch mapsKey={mapsKey} lang={lang} placeholder={c.me.addressPh} onPlace={placeHomePin} autoFocus={addressFocusWanted} />
        <button type="button" className="tj-chip" aria-pressed={pickMode} onClick={startPick}>
          {c.me.pinOnMap}
        </button>
      </div>
    ) : (
      <p className="tm-muted">{c.me.noMaps}</p>
    )
  ) : null;

  return (
    <div className="hp" lang={lang} ref={pageRef}>
      <SiteHeader lang={lang} active="tutors" langHref={TUTORS_PATH} />
      <main className="tm">
        <div className="tm-head tj-wrap">
          <div className="tm-head__title">
            <h1>{c.h1}</h1>
            <span className="tm-muted">{c.lead}</span>
          </div>
          {/* Вход для репетиторов — сразу под заголовком и на телефоне (19.10.2026):
              ссылка «Добавить себя на карту» внизу страницы не находилась. */}
          <div className="tm-joinbar">
            <span className="tm-joinbar__text">{c.joinBannerText}</span>
            <a className="hp-btn hp-btn--lg tm-joinbar__btn" href={TUTORS_JOIN_HREF[lang]} data-cta="tutors_banner_join">
              {c.joinBannerBtn}
            </a>
          </div>
          <div className="tj-kinds" role="group" aria-label={c.title}>
            {(["tutor", "club"] as PartnerKind[]).map((k) => (
              <button key={k} type="button" className="tj-chip tj-chip--kind" aria-pressed={layer === k} onClick={() => pickLayer(k)}>
                {k === "tutor" ? c.layerTutors : c.layerClubs}
              </button>
            ))}
          </div>
        </div>

        {/* Телефон: строка поиска и «Filtrlər» сразу под заголовком (на компьютере скрыта, CSS). */}
        <div className="tm-msearch tj-wrap" role="search">
          <div className="tm-msearch__box">
            <svg className="tm-msearch__icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.2" /><path d="M16 16l4.5 4.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
            <input
              className="tm-msearch__in"
              type="search"
              enterKeyHint="search"
              value={searchText}
              placeholder={u.searchPh}
              aria-label={u.searchPh}
              onChange={(e) => {
                const v = e.target.value;
                setSearchText(v);
                setQuery("");
                if (!v) clearSearchPick();
              }}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                if (suggestions.length === 1) pickSuggestion(suggestions[0]);
                else setQuery(searchText.trim());
                (e.target as HTMLInputElement).blur();
              }}
            />
            {searchText && (
              <button type="button" className="tm-msearch__x" aria-label={u.clearSearch} onClick={clearSearchPick}>
                ×
              </button>
            )}
            {suggestions.length > 0 && (
              <ul className="tm-msearch__sug" role="listbox" aria-label={u.searchPh}>
                {suggestions.map((x) => (
                  <li key={`${x.kind}-${x.code}`}>
                    <button type="button" role="option" aria-selected={false} onClick={() => pickSuggestion(x)}>
                      <span>{x.label}</span>
                      <small>{x.kind === "subject" ? u.sugSubject : x.kind === "district" ? u.sugDistrict : u.sugMetro}</small>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button type="button" className="tm-msearch__filters" aria-expanded={filtersOpen} aria-controls="tm-fpanel" aria-label={u.filtersBtn} title={u.filtersBtn} onClick={() => setFiltersOpen(true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h10M18 7h2M4 17h4M12 17h8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /><circle cx="16" cy="7" r="2.2" fill="none" stroke="currentColor" strokeWidth="2.2" /><circle cx="10" cy="17" r="2.2" fill="none" stroke="currentColor" strokeWidth="2.2" /></svg>
            <span className="tm-msearch__label">{u.filtersBtn}</span>
            {activeFilters > 0 && <b className="tm-msearch__n">{activeFilters}</b>}
          </button>
        </div>

        {/* Ряды фильтров компьютера. На телефоне та же обёртка — выезжающее окно «Filtrlər»
            (display: contents на компьютере: разметка рядов там прежняя). */}
        <div id="tm-fpanel" className={`tm-fpanel${filtersOpen ? " tm-fpanel--open" : ""}`} role={narrow ? "dialog" : undefined} aria-modal={narrow && filtersOpen ? true : undefined} aria-label={narrow ? u.filtersBtn : undefined}>
          {narrow && (
            <>
              <div className="tm-fpanel__head">
                <b>{u.filtersBtn}</b>
                <button type="button" className="tm-fpanel__x" aria-label={c.closeCard} onClick={() => setFiltersOpen(false)}>
                  ×
                </button>
              </div>
              <div className="tj-kinds tm-fpanel__row" role="group" aria-label={c.title}>
                {(["tutor", "club"] as PartnerKind[]).map((k) => (
                  <button key={k} type="button" className="tj-chip tj-chip--kind" aria-pressed={layer === k} onClick={() => pickLayer(k)}>
                    {k === "tutor" ? c.layerTutors : c.layerClubs}
                  </button>
                ))}
              </div>
              <div className="tm-tabs tm-fpanel__row" role="tablist">
                {(["near", "online"] as const).map((t) => (
                  <button key={t} type="button" role="tab" aria-selected={tab === t} className="tj-chip" aria-pressed={tab === t} onClick={() => setTab(t)}>
                    {t === "near" ? c.tabNear : c.tabOnline}
                  </button>
                ))}
              </div>
            </>
          )}
        <div className="tm-filters tj-wrap" role="group">
          <select className="tj-in" value={subject} onChange={(e) => setSubject(e.target.value)} aria-label={layer === "tutor" ? c.subjectAll : c.directionAll}>
            <option value="">{layer === "tutor" ? c.subjectAll : c.directionAll}</option>
            {/* Услуги верхнего уровня (homework) — над категориями предметов. */}
            {servicesFor(layer).map((x) => (
              <option key={x.code} value={x.code}>
                {x[lang]}
              </option>
            ))}
            {categoriesFor(layer).map((cat) => (
              <optgroup key={cat.code} label={cat[lang]}>
                {cat.items.map((x) => (
                  <option key={x.code} value={x.code}>
                    {x[lang]}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <select className="tj-in" value={city} onChange={(e) => pickCity(e.target.value)} aria-label={c.city}>
            {/* Порядок: Bakı, «Bütün ölkə», дальше города с анкетами по справочнику. */}
            {cities.flatMap((code) => [
              <option key={code} value={code}>
                {cityLabel(ref, code, lang)}
              </option>,
              ...(code === BAKU_CITY ? [<option key={ALL_CITIES} value={ALL_CITIES}>{c.cityAll}</option>] : []),
            ])}
          </select>
          {/* Районы — бакинские: при «Bakı» и «Hamısı»; вне Баку анкета — на весь город. */}
          {(baku || city === ALL_CITIES) && (
            <select className="tj-in" value={district} onChange={(e) => setDistrict(e.target.value)} aria-label={c.districtAll}>
              <option value="">{c.districtAll}</option>
              {DISTRICTS.map((d) => (
                <option key={d.code} value={d.code}>
                  {d[lang]}
                </option>
              ))}
            </select>
          )}
          {layer === "tutor" && (baku || city === ALL_CITIES) && (
            <select className="tj-in" value={home} onChange={(e) => setHome(e.target.value)} aria-label={c.comesHomeAll}>
              <option value="">{c.comesHomeAll}</option>
              {DISTRICTS.map((d) => (
                <option key={d.code} value={d.code}>
                  {d.code === home ? `${c.comesHomeAll}: ${d[lang]}` : d[lang]}
                </option>
              ))}
            </select>
          )}
          {layer === "tutor" ? (
            <select className="tj-in" value={grade} onChange={(e) => setGrade(e.target.value)} aria-label={c.gradeAll}>
              <option value="">{c.gradeAll}</option>
              {GRADES.map((g) => (
                <option key={g.code} value={g.code}>
                  {g[lang]}
                </option>
              ))}
            </select>
          ) : (
            <input className="tj-in" type="number" min={1} max={99} inputMode="numeric" value={age} onChange={(e) => setAge(e.target.value)} placeholder={c.agePh} aria-label={c.agePh} />
          )}
          <select className="tj-in" value={format} onChange={(e) => setFormat(e.target.value)} aria-label={c.formatAll}>
            <option value="">{c.formatAll}</option>
            {FORMATS.map((f) => (
              <option key={f.code} value={f.code}>
                {f[lang]}
              </option>
            ))}
          </select>
          <input className="tj-in" type="number" min={0} inputMode="numeric" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} placeholder={c.priceMaxPh} aria-label={c.priceMaxPh} />
          <label className="tm-check">
            <input type="checkbox" checked={trial} onChange={() => setTrial(!trial)} />
            {c.trialOnly}
          </label>
          <button type="button" className="tj-plain" onClick={reset}>
            {c.reset}
          </button>
        </div>

        {/* Быстрые запросы для «Репетиторы»: первый чип — homework (map-taxonomy.json → tutor_quick). */}
        {layer === "tutor" && (
          <div className="tm-quick tj-wrap" role="group" aria-label={c.subjectAll}>
            {TUTOR_QUICK.map((code) => (
              <button key={code} type="button" className="tj-chip" data-quick={code} aria-pressed={subject === code} onClick={() => setSubject(subject === code ? "" : code)}>
                {labelOf(subjectItemsFor("tutor"), code, lang)}
              </button>
            ))}
          </div>
        )}
          {narrow && (
            <>
              {tab === "near" && (
                <div className="tm-me tm-fpanel__me">
                  <div className="tm-me__row">
                    {mapsKey && !askAddress && !pickMode && (
                      <button type="button" className="tj-plain" onClick={() => setAskAddress(true)}>
                        {c.me.addressPh}
                      </button>
                    )}
                    {meNow}
                  </div>
                  {meAddress}
                </div>
              )}
              <div className="tm-fpanel__foot">
                <button type="button" className="tj-plain" onClick={reset}>
                  {c.reset}
                </button>
                <button type="button" className="hp-btn tm-fpanel__show" onClick={() => setFiltersOpen(false)}>
                  {u.showN(shown.length)}
                </button>
              </div>
            </>
          )}
        </div>
        {narrow && filtersOpen && <button type="button" className="tm-fpanel__dim" aria-label={c.closeCard} onClick={() => setFiltersOpen(false)} />}

        <div className="tm-split" ref={splitRef}>
          <section className="tm-list" aria-live="polite" ref={sheetRef} data-sheet={sheet}>
            {/* Телефон: ручка шторки и «N repetitor». На компьютере не рисуется. */}
            {narrow && (
              <div className="tm-sheethead" role="button" tabIndex={0} aria-expanded={sheet === "full"} aria-label={u.sheetHandle} onClick={() => setSheet((s) => (s === "full" ? "peek" : "full"))} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setSheet((s) => (s === "full" ? "peek" : "full")))}>
                <span className="tm-sheethead__grip" aria-hidden="true" />
                <b>{q.isSuccess ? u.countOf(layer, shown.length) : "…"}</b>
              </div>
            )}
            {tab === "near" && (
              <div className="tm-me" data-testid="tm-me">
                <div className="tm-me__row">
                  <button type="button" className="tj-chip tm-me__btn" onClick={locate} disabled={meStatus === "locating"}>
                    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4" fill="currentColor" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><circle cx="12" cy="12" r="7.5" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
                    {meStatus === "locating" ? c.me.locating : c.me.button}
                  </button>
                  {mapsKey && !askAddress && !pickMode && (
                    <button type="button" className="tj-plain" onClick={() => setAskAddress(true)}>
                      {c.me.addressPh}
                    </button>
                  )}
                  {meNow}
                </div>
                {meNotice}
                {/* Поле адреса — здесь на компьютере, в окне «Filtrlər» на телефоне (одно на страницу). */}
                {!narrow && meAddress}
                {pickMode && <p className="tm-muted" role="status">{c.me.pinHint}</p>}
              </div>
            )}
            <div className="tm-list__bar">
              <div className="tm-tabs" role="tablist">
                {(["near", "online"] as const).map((t) => (
                  <button key={t} type="button" role="tab" aria-selected={tab === t} className="tj-chip" aria-pressed={tab === t} onClick={() => setTab(t)}>
                    {t === "near" ? c.tabNear : c.tabOnline}
                  </button>
                ))}
              </div>
              <span className="tm-muted">{q.isSuccess ? c.count(shown.length) : ""}</span>
            </div>
            {savedIds.length > 0 ? (
              <div className="tm-list__extra">
                {savedIds.length > 0 && (
                  <a className="tm-savedlink" href={`${TUTORS_PATH[lang]}/saved`}>
                    ♥ {u.savedLink(savedIds.length)}
                  </a>
                )}
              </div>
            ) : null}
            {q.isError && <p className="tm-err">{c.loadError}</p>}
            {q.isSuccess && shown.length === 0 ? (
              <RequestForm lang={lang} layer={layer} title={c.emptyTitle} preset={preset} />
            ) : (
              <>
                {featured.length > 0 && (
                  <section className="tm-featured" aria-label={FEATURED_COPY[lang].title}>
                    <h2 className="tm-featured__title">{FEATURED_COPY[lang].title}</h2>
                    {featured.map(({ p }) => (
                      <PartnerCard key={p.id} p={p} lang={lang} featured track="featured" active={p.id === activeId} onPick={() => setActiveId(p.id)} distance={kmOf.get(p.id) != null ? formatKm(kmOf.get(p.id)!, lang) : null} hover={p.id === hoverId} onHover={setHoverId} />
                    ))}
                  </section>
                )}
                {/* Пока партнёров мало — заявка над списком, свёрнутая, чтобы не прятать карточки. */}
                {q.isSuccess && rows.length < 30 && (
                  <details className="tm-req-wrap">
                    <summary>{c.notFoundTitle}</summary>
                    <RequestForm lang={lang} layer={layer} title={c.notFoundTitle} preset={preset} hideTitle />
                  </details>
                )}
                {placed.map(({ p, km }) => (
                  <PartnerCard key={p.id} p={p} lang={lang} active={p.id === activeId} onPick={() => setActiveId(p.id)} distance={km !== null ? formatKm(km, lang) : null} hover={p.id === hoverId} onHover={setHoverId} track="list" narrowOnly={p.id === fourth} />
                ))}
                {placedUnknown.length > 0 && (
                  <p className="tm-muted tm-price-unknown" role="note">
                    {c.priceUnknown}
                  </p>
                )}
                {placedUnknown.map(({ p, km }) => (
                  <PartnerCard key={p.id} p={p} lang={lang} active={p.id === activeId} onPick={() => setActiveId(p.id)} distance={km !== null ? formatKm(km, lang) : null} hover={p.id === hoverId} onHover={setHoverId} track="list" narrowOnly={p.id === fourth} />
                ))}
                {q.isSuccess && rows.length >= 30 && <RequestForm lang={lang} layer={layer} title={c.notFoundTitle} preset={preset} />}
              </>
            )}
            <p className="tm-muted tm-note">{c.separate}</p>
            <a className="tm-link tm-link--wide" href={TUTORS_JOIN_HREF[lang]}>
              {c.joinCta}
            </a>
            {/* Телефон: плашка «Xəritəyə əlavə ol» убрана с первого экрана — ссылка здесь и в меню ☰. */}
            <a className="tm-link tm-link--narrow" href={TUTORS_JOIN_HREF[lang]} data-cta="tutors_list_join">
              {u.joinListLink}
            </a>
          </section>
          <section className="tm-mapcol">
            {tab === "near" && (
              <button type="button" className="tm-map__me" onClick={locate} aria-label={c.me.button} title={c.me.button} disabled={meStatus === "locating"}>
                <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4" fill="currentColor" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><circle cx="12" cy="12" r="7.5" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
              </button>
            )}
            {pickMode && <span className="tm-map__pickhint" role="status">{c.me.pinHint}</span>}
            {mapsKey !== undefined && (
              <MapCanvas
                mapsKey={mapsKey}
                lang={lang}
                points={points}
                activeId={activeId}
                onSelect={setActiveId}
                onClose={closeCard}
                popup={activeP ? <MapPopupCard p={activeP} lang={lang} onClose={closeCard} /> : null}
                labels={{ loading: "…", error: c.loadError }}
                me={me}
                meLabel={{ gps: c.me.youAreHere, home: c.me.home }}
                pickMode={pickMode}
                onPick={placeHomePin}
                focus={focus}
                gestures="greedy"
                hoverId={hoverId}
                onHover={setHoverId}
                onBounds={setArea}
              />
            )}
            {/* Кого на карте нет: онлайн и выезд без точки — и где их искать. */}
            {tab === "near" && layer === "tutor" && !(narrow && meNotice) && !pickMode && <p className="tm-map__off">{c.mapOffNote}</p>}
            {/* Телефон: список свёрнут — сообщение об отказе геолокации поверх карты. */}
            {narrow && meNotice && <div className="tm-map__notice">{meNotice}</div>}
            {/* Телефон: карточка выбранной метки — над свёрнутой шторкой (на компьютере — окошко у метки). */}
            {narrow && activeP && (
              <div className="tm-sheet">
                <MapPopupCard p={activeP} lang={lang} onClose={closeCard} />
              </div>
            )}
          </section>
        </div>

        {/* Интро и страницы предметов — под картой, чтобы на телефоне не отодвигать её. Рисуется сервером. */}
        <section className="tm-about tj-wrap">
          {c.intro.map((t) => (
            <p key={t.slice(0, 24)}>{t}</p>
          ))}
          <p className="tm-about__links">
            <a className="hp-btn" href={`/start?lang=${lang}&pkg=basic`} data-cta="tutors_about_test">{c.introTest}</a>
            <a className="hp-btn hp-btn--outline" href={TUTORS_JOIN_HREF[lang]} data-cta="tutors_about_join">{c.introJoin}</a>
          </p>
          {subjectPages.length > 0 && (
            <nav aria-label={c.subjectPagesTitle}>
              <h2>{c.subjectPagesTitle}</h2>
              <ul className="tm-about__subjects">
                {subjectPages.map((code) => (
                  <li key={code}>
                    <a href={subjectPath(code, lang)}>{subjectLabel(code, lang)}</a>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </section>
      </main>
      <SiteFooter lang={lang} active="tutors" langHref={TUTORS_PATH} />
    </div>
  );
}
